import { useEffect, useMemo, useState } from "react";
import { PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { listarBandejaAutorizacion } from "../services/bandeja-service";
import { BANDEJA_PAGE_SIZE, useBandejaAutorizacion } from "../hooks/use-bandeja-autorizacion";
import { useNotificarLlegada } from "../hooks/use-notificar-llegada";
import { filtrosNoVacios, type BandejaFiltros } from "../utils/ordenar-bandeja";
import { BandejaHeader } from "./BandejaHeader";
import { BandejaDesktopTable } from "./BandejaDesktopTable";
import { BandejaMobileList } from "./BandejaMobileList";
import { BandejaVaciaCard } from "./BandejaEmptyState";

/**
 * Página de la bandeja de autorización · listado con polling. El filtrado es por
 * columna (fila de búsqueda bajo los encabezados): se aplica en el SERVIDOR (query
 * params, con debounce) y también en el cliente para feedback instantáneo. El orden
 * es por columna dentro de la tabla y el PAGINADO se resuelve en el servidor
 * (envelope {items, total}). La query se precarga en el loader del route.
 */
export function BandejaAutorizacionPage() {
  const [filtros, setFiltros] = useState<BandejaFiltros>({});
  const [page, setPage] = useState(1);
  const [filtrosDebounced] = useDebouncedValue(filtros, 350);

  const serverParams = useMemo(
    () => ({
      ...filtrosNoVacios(filtrosDebounced),
      page,
      pageSize: BANDEJA_PAGE_SIZE,
    }),
    [filtrosDebounced, page],
  );

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtrosDebounced]);

  const { data, refetch, isFetching, isPlaceholderData } =
    useBandejaAutorizacion(serverParams);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const { permiso, solicitar } = useNotificarLlegada(total);

  const hayFiltros = Object.keys(filtrosNoVacios(filtrosDebounced)).length > 0;

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodas = async (): Promise<BandejaAutorizacionItem[]> => {
    const params = filtrosNoVacios(filtrosDebounced);
    const todas: BandejaAutorizacionItem[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarBandejaAutorizacion({ ...params, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <BandejaHeader
        permiso={permiso}
        onActivarNotificaciones={solicitar}
        fetching={isFetching}
        onRefrescar={() => void refetch()}
        obtenerFilas={obtenerTodas}
      />
      <Stack gap="md">
        {total === 0 && !hayFiltros ? (
          <BandejaVaciaCard />
        ) : (
          <>
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={BANDEJA_PAGE_SIZE}
              onPageChange={setPage}
              variante="superior"
            />
            <BandejaDesktopTable
              items={items}
              filtros={filtros}
              buscando={isPlaceholderData}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
            />
            <BandejaMobileList items={items} />
            <PaginadorTabla
              total={total}
              page={page}
              pageSize={BANDEJA_PAGE_SIZE}
              onPageChange={setPage}
              sustantivo="solicitudes"
            />
          </>
        )}
      </Stack>
    </div>
  );
}
